'use strict';

import { initItemsDemo } from './items-demo.js';
import { isEnabled } from './features/flags.js';
import {
  createItem,
  getItems,
  inboxForUser,
  statsForRole,
  adminStats,
  updateItem,
} from './features/items/store.js';
import {
  renderUserInbox,
  renderCreateItemForm,
  renderDashboardStats,
  renderAdminOverviewStats,
} from './features/items/ui.js';

const historyStack = ['home'];
const DEMO_USER = 'demo-user';

function inferRole() {
  if (!document.getElementById('screen-role-admin')?.classList.contains('hidden')) return 'admin';
  if (!document.getElementById('screen-role-user')?.classList.contains('hidden')) return 'user';
  if (!document.getElementById('screen-role-viewer')?.classList.contains('hidden')) return 'viewer';
  return 'user';
}

/**
 * Render the local `item-lifecycle` demo slice. This is gated by the
 * `item-lifecycle` feature flag and operates entirely in the browser so
 * the demo works without the mock API server.
 */
export function renderSlices() {
  if (!isEnabled('item-lifecycle')) return;

  const role = inferRole();

  const adminStatsContainer = document.getElementById('admin-overview-stats');
  if (adminStatsContainer && role === 'admin') {
    renderAdminOverviewStats(adminStatsContainer, adminStats());
  }

  const inboxContainer = document.getElementById('user-inbox-list');
  if (inboxContainer && role === 'user') {
    renderUserInbox(inboxContainer, inboxForUser(DEMO_USER), (id) => {
      const item = getItems().find((i) => i.id === id);
      if (item) {
        updateItem(id, { status: item.status === 'done' ? 'todo' : 'done' });
      }
      renderSlices();
    });
  }

  const createFormContainer = document.getElementById('user-create-form');
  if (createFormContainer && role === 'user') {
    renderCreateItemForm(createFormContainer, (fields) => {
      createItem({ ...fields, createdBy: DEMO_USER, assignees: [DEMO_USER] });
      renderSlices();
    });
  }

  const viewerStatsContainer = document.getElementById('viewer-dashboard-stats');
  if (viewerStatsContainer && role === 'viewer') {
    renderDashboardStats(viewerStatsContainer, statsForRole('viewer'));
  }
}

export function showScreen(id) {
  document.querySelectorAll('.screen').forEach((el) => el.classList.add('hidden'));
  const target = document.getElementById('screen-' + id);
  if (target) {
    target.classList.remove('hidden');
    try {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      // Scroll-to is not available in some test environments; ignore.
    }
  }

  document.querySelectorAll('.topnav button').forEach((btn) => {
    btn.classList.toggle('active', btn.dataset.screen === id);
  });

  const back = document.getElementById('nav-back');
  if (back) {
    back.style.visibility = id === 'home' ? 'hidden' : 'visible';
  }

  renderSlices();
}

export function navigateTo(id) {
  if (historyStack[historyStack.length - 1] !== id) {
    historyStack.push(id);
  }
  showScreen(id);
}

export function goBack() {
  if (historyStack.length > 1) {
    historyStack.pop();
    showScreen(historyStack[historyStack.length - 1]);
  }
}

export function activateJourney(panelId, journeyId) {
  const panel = document.getElementById(panelId);
  if (!panel) return;
  panel.querySelectorAll('.journey').forEach((el) => el.classList.add('hidden'));
  const target = document.getElementById('journey-' + journeyId);
  if (target) {
    target.classList.remove('hidden');
    renderSlices();
  }

  const sidebar = panel.previousElementSibling;
  if (sidebar && sidebar.classList.contains('sidebar')) {
    sidebar.querySelectorAll('.sidebar-item').forEach((btn) => {
      btn.classList.toggle('active', btn.dataset.journey === journeyId);
    });
  }
}

export async function loadWireframes() {
  const grid = document.getElementById('wireframe-grid');
  if (!grid) return;

  try {
    const res = await fetch('wireframes/');
    if (!res.ok) throw new Error('Wireframe index unavailable');
    const html = await res.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const links = Array.from(doc.querySelectorAll('a'))
      .map((a) => a.getAttribute('href'))
      .filter((name) => name && /\.(png|jpg|jpeg|webp|gif|svg)$/i.test(name));

    if (links.length === 0) {
      grid.innerHTML = '<p class="empty">No wireframes uploaded yet. Add images to <code>mockup/wireframes/</code>.</p>';
      return;
    }

    grid.innerHTML = '';
    for (const name of links) {
      const card = document.createElement('div');
      card.className = 'wireframe-card';

      const img = document.createElement('img');
      img.src = `wireframes/${name}`;
      img.alt = name;
      img.loading = 'lazy';

      const caption = document.createElement('div');
      caption.className = 'caption';
      caption.textContent = decodeURIComponent(name);

      card.appendChild(img);
      card.appendChild(caption);
      grid.appendChild(card);
    }
  } catch {
    grid.innerHTML = '<p class="empty">Wireframe gallery unavailable offline.</p>';
  }
}

function updateFeatureFlagBanner() {
  const banner = document.getElementById('feature-flag-banner');
  const footer = document.getElementById('feature-flag-footer');
  if (isEnabled('item-lifecycle')) {
    if (banner) banner.innerHTML = '<span>Demo feature <strong>item-lifecycle</strong> is active.</span>';
    if (footer) footer.innerHTML = '<span>Demo feature active</span>';
  }
}

export function initApp() {
  document.addEventListener('click', (e) => {
    const screenTrigger = e.target.closest('[data-screen]');
    if (screenTrigger) {
      e.preventDefault();
      navigateTo(screenTrigger.dataset.screen);
      return;
    }

    const journeyTrigger = e.target.closest('[data-journey]');
    if (journeyTrigger) {
      e.preventDefault();
      const panel = journeyTrigger.closest('.dashboard')?.querySelector('.panel');
      if (panel) {
        activateJourney(panel.id, journeyTrigger.dataset.journey);
      }
    }
  });

  document.getElementById('nav-back')?.addEventListener('click', goBack);

  updateFeatureFlagBanner();
  showScreen('home');
  loadWireframes();
  initItemsDemo().catch(() => {});
}

initApp();
